/**
 * Credit Pricing — Client Preview
 *
 * Mirrors the server's creditPricing.js so the UI can show an
 * instant cost estimate next to the Generate button.
 *
 * IMPORTANT: The server is the single source of truth. These values
 * are for display only — the server recalculates and validates before
 * deducting credits.
 */

// ── Costs (must stay in sync with server/src/config/creditPricing.js) ───────

export const CREDIT_COSTS = {
  image: 4,
  videoWithAudio: 12,   // per second of generated video
  videoSilent: 6,       // per second of generated video
  soundEffect: 2,       // per second of generated audio
  voiceover: {
    per1000Chars: 10,
    minimum: 2,
  },
};

// Generation types whose cost is multiplied by duration (seconds)
const PER_SECOND_TYPES = new Set(['videoWithAudio', 'videoSilent', 'soundEffect']);

// Default duration (seconds) when no length is provided for per-second types
const DEFAULT_DURATION_SECONDS = 5;

// Model ID → cost type
const MODEL_TYPE_MAP = {
  'nano-banana':   'image',
  'veo-audio':     'videoWithAudio',
  'veo-silent':    'videoSilent',
  'kling-audio':   'videoWithAudio',
  'kling-silent':  'videoSilent',
  'sound-effects': 'soundEffect',
  'voiceover':     'voiceover',
};

/**
 * Parse a duration string (e.g. '5s', '10s', '8') into seconds.
 * Falls back to DEFAULT_DURATION_SECONDS if the string is unrecognised.
 *
 * @param {string|number} lengthStr
 * @returns {number} seconds
 */
export function parseDuration(lengthStr) {
  if (typeof lengthStr === 'number' && lengthStr > 0) return lengthStr;
  if (!lengthStr) return DEFAULT_DURATION_SECONDS;
  const match = String(lengthStr).match(/^(\d+(?:\.\d+)?)s?$/);
  if (match) return parseFloat(match[1]);
  return DEFAULT_DURATION_SECONDS;
}

/**
 * Estimate the credit cost for a given model + prompt + duration + output count.
 *
 * @param {string} modelId  – Client model ID (e.g. 'veo-audio')
 * @param {object} [opts]
 * @param {string} [opts.prompt] – Prompt text (used for voiceover char count)
 * @param {string|number} [opts.length] – Duration string (e.g. '5s') for video/sound types
 * @param {number} [opts.outputCount] – Number of outputs requested (default 1)
 * @returns {number|null} – Estimated cost, or null if model is unknown
 */
export function estimateCreditCost(modelId, opts = {}) {
  const type = MODEL_TYPE_MAP[modelId];
  if (!type) return null;

  const outputCount = Math.max(1, parseInt(opts.outputCount, 10) || 1);

  if (type === 'voiceover') {
    const chars = opts.prompt ? opts.prompt.length : 0;
    const calculated = Math.ceil(chars / 1000) * CREDIT_COSTS.voiceover.per1000Chars;
    return Math.max(calculated, CREDIT_COSTS.voiceover.minimum) * outputCount;
  }

  const perUnitCost = CREDIT_COSTS[type];

  // Per-second types: multiply the per-second rate by the requested duration
  if (PER_SECOND_TYPES.has(type)) {
    const seconds = parseDuration(opts.length);
    return perUnitCost * seconds * outputCount;
  }

  return perUnitCost * outputCount;
}

/**
 * Get the cost type label for a model (useful for tooltips).
 */
export function getCostType(modelId) {
  return MODEL_TYPE_MAP[modelId] || null;
}
